import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/api/homework`;

export type HomeworkAssignType = "ALL" | "SELECTED";

export interface HomeworkAttachment {
  id: number;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  contentType: string;
}

export interface HomeworkRequestDto {
  schoolId: number;
  classId: number;
  className?: string;
  subjectId: number;
  subjectName?: string;
  sessionId: number;
  sessionName?: string;
  assignmentDate: string; // yyyy-MM-dd
  title: string;
  description: string;
  dueDate: string; // yyyy-MM-dd
  assignType: HomeworkAssignType;
  studentIds?: number[];
  // Ids of previously-saved attachments to delete as part of this update — processed
  // server-side in the same request as any newly uploaded files (edit mode only).
  removeAttachmentIds?: number[];
}

export interface HomeworkResponseDto {
  id: number;
  classId: number;
  className: string;
  subjectId: number;
  subjectName: string;
  sessionId: number;
  sessionName: string;
  assignmentDate: string;
  title: string;
  description: string;
  dueDate: string;
  assignType: HomeworkAssignType;
  assignedByName: string;
  createdAt: string;
  attachments: HomeworkAttachment[];
  studentIds?: number[];
}

export interface HomeworkSummaryDto {
  id: number;
  classId: number;
  className: string;
  subjectId: number;
  subjectName: string;
  sessionId: number;
  sessionName: string;
  title: string;
  assignmentDate: string;
  dueDate: string;
  assignType: HomeworkAssignType;
  assignedByName: string;
  attachmentCount: number;
}

// Backend expects the DTO as a JSON Blob part ("homework") plus files under "attachments"
const buildHomeworkFormData = (dto: HomeworkRequestDto, files: File[]): FormData => {
  const formData = new FormData();
  const jsonBlob = new Blob([JSON.stringify(dto)], { type: "application/json" });
  formData.append("homework", jsonBlob);
  files.forEach((file) => formData.append("attachments", file, file.name));
  return formData;
};

const createHomework = async (
  dto: HomeworkRequestDto,
  files: File[] = []
): Promise<HomeworkResponseDto> => {
  const formData = buildHomeworkFormData(dto, files);
  const response = await axios.post<HomeworkResponseDto>(API_URL, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const updateHomework = async (
  id: number,
  dto: HomeworkRequestDto,
  files: File[] = []
): Promise<HomeworkResponseDto> => {
  const formData = buildHomeworkFormData(dto, files);
  const response = await axios.put<HomeworkResponseDto>(`${API_URL}/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const getHomeworkById = async (id: number): Promise<HomeworkResponseDto> => {
  const response = await axios.get<HomeworkResponseDto>(`${API_URL}/${id}`);
  return response.data;
};

const getHomeworkBySchool = async (schoolId: number): Promise<HomeworkSummaryDto[]> => {
  const response = await axios.get<HomeworkSummaryDto[]>(`${API_URL}/school/${schoolId}`);
  return response.data;
};

const getHomeworkByClass = async (
  schoolId: number,
  classId: number,
  subjectId?: number
): Promise<HomeworkSummaryDto[]> => {
  const response = await axios.get<HomeworkSummaryDto[]>(
    `${API_URL}/school/${schoolId}/class/${classId}`,
    { params: subjectId ? { subjectId } : undefined }
  );
  return response.data;
};

const deleteHomework = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

const deleteHomeworkAttachment = async (id: number, attachmentId: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}/attachment/${attachmentId}`);
};

export const downloadHomeworkAttachment = async (
  fileName: string,
  originalFileName: string
): Promise<void> => {
  const response = await axios.get(`${API_URL}/attachment/${fileName}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = originalFileName || fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// --- Student-facing "my homework" endpoints (identity inferred from JWT, no schoolId/classId needed) ---

const getMyHomework = async (): Promise<HomeworkSummaryDto[]> => {
  const response = await axios.get<HomeworkSummaryDto[]>(`${API_URL}/my`);
  return response.data;
};

const getMyHomeworkById = async (id: number): Promise<HomeworkResponseDto> => {
  const response = await axios.get<HomeworkResponseDto>(`${API_URL}/my/${id}`);
  return response.data;
};

export const downloadMyHomeworkAttachment = async (
  fileName: string,
  originalFileName: string
): Promise<void> => {
  const response = await axios.get(`${API_URL}/my/attachment/${fileName}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = originalFileName || fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export function useHomeworkBySchool(schoolId?: number, enabled: boolean = true) {
  return useQuery<HomeworkSummaryDto[], Error>({
    queryKey: ["homework", "school", schoolId],
    queryFn: () => getHomeworkBySchool(schoolId as number),
    enabled: !!schoolId && enabled,
    staleTime: 30000,
  });
}

export function useHomeworkByClass(schoolId?: number, classId?: number, subjectId?: number) {
  return useQuery<HomeworkSummaryDto[], Error>({
    queryKey: ["homework", "class", schoolId, classId, subjectId],
    queryFn: () => getHomeworkByClass(schoolId as number, classId as number, subjectId),
    enabled: !!schoolId && !!classId,
    staleTime: 30000,
  });
}

export function useHomeworkDetail(id?: number) {
  return useQuery<HomeworkResponseDto, Error>({
    queryKey: ["homework", "detail", id],
    queryFn: () => getHomeworkById(id as number),
    enabled: !!id,
  });
}

export function useMyHomework() {
  return useQuery<HomeworkSummaryDto[], Error>({
    queryKey: ["homework", "my"],
    queryFn: getMyHomework,
    staleTime: 30000,
    // Poll every 30 seconds so newly assigned homework appears automatically, matching MyTasksPage.tsx
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useMyHomeworkDetail(id?: number) {
  return useQuery<HomeworkResponseDto, Error>({
    queryKey: ["homework", "my", "detail", id],
    queryFn: () => getMyHomeworkById(id as number),
    enabled: !!id,
  });
}

export function useHomeworkMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["homework"] });
  };

  const createHomeworkMutation = useMutation({
    mutationFn: ({ dto, files }: { dto: HomeworkRequestDto; files: File[] }) =>
      createHomework(dto, files),
    onSuccess: invalidate,
  });

  const updateHomeworkMutation = useMutation({
    mutationFn: ({ id, dto, files }: { id: number; dto: HomeworkRequestDto; files: File[] }) =>
      updateHomework(id, dto, files),
    onSuccess: invalidate,
  });

  const deleteHomeworkMutation = useMutation({
    mutationFn: (id: number) => deleteHomework(id),
    onSuccess: invalidate,
  });

  const deleteHomeworkAttachmentMutation = useMutation({
    mutationFn: ({ id, attachmentId }: { id: number; attachmentId: number }) =>
      deleteHomeworkAttachment(id, attachmentId),
    onSuccess: invalidate,
  });

  return {
    createHomeworkMutation,
    updateHomeworkMutation,
    deleteHomeworkMutation,
    deleteHomeworkAttachmentMutation,
  };
}
